import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import deleteSiteArtifact from "../http/deleteSiteArtifact";
import getSiteArtifactDownloadUrl from "../http/getSiteArtifactDownloadUrl";
import getSiteArtifacts, { SiteArtifact } from "../http/getSiteArtifacts";
import getSiteArtifactUploadUrl from "../http/getSiteArtifactUploadUrl";
import promoteSiteArtifact from "../http/promoteSiteArtifact";
import putSiteFile from "../http/putSiteFile";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

interface UseSiteArtifactsParams {
  siteId: string;
  enabled?: boolean;
}

const useSiteArtifacts = ({
  siteId,
  enabled = true,
}: UseSiteArtifactsParams) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();

  const fetchArtifacts = useQuery(
    ["siteArtifacts", siteId],
    async () => {
      return getSiteArtifacts(
        await getAuthTokenBlocking(getToken, relogin),
        siteId
      );
    },
    {
      enabled,
    }
  );

  const uploadArtifact = useMutation(
    async (file: File) => {
      const { name, type: contentType } = file;
      const authToken = await getAuthTokenBlocking(getToken, relogin);

      // Get pre-signed upload URL
      const uploadUrlResponse = await getSiteArtifactUploadUrl(
        authToken,
        siteId,
        contentType,
        name
      );

      // Upload file to pre-signed URL
      await putSiteFile(uploadUrlResponse.url, contentType, file);

      // Promote the artifact to long-term storage
      const promoteResponse = await promoteSiteArtifact(
        authToken,
        siteId,
        uploadUrlResponse.artifactGuid
      );

      return promoteResponse.artifact;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["siteArtifacts", siteId]);
      },
    }
  );

  const deleteArtifact = useMutation(
    async (artifactGuid: string) => {
      await deleteSiteArtifact(
        await getAuthTokenBlocking(getToken, relogin),
        siteId,
        artifactGuid
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["siteArtifacts", siteId]);
      },
    }
  );

  const getDownloadUrl = async (artifactGuid: string): Promise<string> => {
    const response = await getSiteArtifactDownloadUrl(
      await getAuthTokenBlocking(getToken, relogin),
      siteId,
      artifactGuid
    );
    return response.url;
  };

  return {
    fetchArtifacts,
    uploadArtifact,
    deleteArtifact,
    getDownloadUrl,
  };
};

export default useSiteArtifacts;

export type { SiteArtifact };
